<template>
  <div v-if="aberto" class="aotp">
    <div class="aotp__header">
      <div>
        <h5 class="aotp__title">Testar condição (dry-run)</h5>
        <p class="aotp__hint">
          Preencha os valores das variáveis do evento e dispare o teste. A condição será avaliada em transação isolada — nenhuma ação real será executada.
        </p>
      </div>
      <button type="button" class="aotp__icon-btn" @click="fechar" title="Fechar">
        <fa-icon icon="times" />
      </button>
    </div>

    <div v-if="!fields.length" class="aotp__empty">
      <fa-icon icon="info-circle" /> Nenhuma variável disponível para este evento. O teste será executado com payload vazio.
    </div>

    <template v-else>
      <template v-if="camposCondicaoAgrupados.length">
        <div class="aotp__group-label">Variáveis usadas nas condições</div>
        <div
          v-for="g in camposCondicaoAgrupados"
          :key="'cond-' + g.nome"
          class="aotp__model-group"
        >
          <div class="aotp__model-name">{{ g.nome }}</div>
          <div class="aotp__grid">
            <div v-for="f in g.campos" :key="f.name" class="aotp__field">
              <label class="aotp__label">
                {{ f.label }}
                <code class="aotp__var">{{ f.name }}</code>
              </label>
              <input
                type="text"
                class="form-control aotp__input"
                :value="testValues[f.name]"
                @input="setTestValue(f.name, $event.target.value)"
                :placeholder="`Valor de ${f.name}`"
              />
            </div>
          </div>
        </div>
      </template>
      <div v-else class="aotp__empty">
        <fa-icon icon="info-circle" /> Nenhuma variável referenciada nas condições atuais. Adicione variáveis abaixo para informar valores manualmente, ou dispare o teste com payload vazio.
      </div>

      <template v-if="extrasAdicionadosAgrupados.length">
        <div class="aotp__group-label aotp__group-label--extras">Outras variáveis adicionadas</div>
        <div
          v-for="g in extrasAdicionadosAgrupados"
          :key="'extra-' + g.nome"
          class="aotp__model-group"
        >
          <div class="aotp__model-name">{{ g.nome }}</div>
          <div class="aotp__grid">
            <div v-for="f in g.campos" :key="f.name" class="aotp__field">
              <label class="aotp__label">
                {{ f.label }}
                <code class="aotp__var">{{ f.name }}</code>
                <button
                  type="button"
                  class="aotp__remove"
                  @click="removerExtra(f.name)"
                  :title="`Remover ${f.name}`"
                >
                  <fa-icon icon="times" />
                </button>
              </label>
              <input
                type="text"
                class="form-control aotp__input"
                :value="testValues[f.name]"
                @input="setTestValue(f.name, $event.target.value)"
                :placeholder="`Valor de ${f.name}`"
              />
            </div>
          </div>
        </div>
      </template>

      <div v-if="camposDisponiveisParaAdicionar.length" class="aotp__add">
        <button
          v-if="!adicionando"
          type="button"
          class="aotp__toggle"
          @click="iniciarAdicao"
        >
          <fa-icon icon="plus" />
          <span>Adicionar variável de teste ({{ camposDisponiveisParaAdicionar.length }} disponíveis)</span>
        </button>
        <div v-else class="aotp__add-picker">
          <acao-objeto-campo-select
            ref="addPicker"
            :fields="camposDisponiveisParaAdicionar"
            :value="buscaCampo"
            placeholder="Selecione uma variável..."
            @input="onCampoSelecionado"
          />
          <button type="button" class="aotp__toggle aotp__toggle--cancel" @click="cancelarAdicao">
            <fa-icon icon="times" /> <span>Cancelar</span>
          </button>
        </div>
      </div>
    </template>

    <div v-if="testResult" class="aotp__result" :class="testResultClass">
      <div class="aotp__result-head">
        <fa-icon :icon="testResult.icon" />
        <strong>{{ testResult.label }}</strong>
        <span v-if="testResult.detail">— {{ testResult.detail }}</span>
      </div>
      <ul v-if="testResult.motivos && testResult.motivos.length" class="aotp__motivos">
        <li v-for="(m, i) in testResult.motivos" :key="i">
          <fa-icon icon="times-circle" /> {{ m }}
        </li>
      </ul>
    </div>

    <div class="aotp__footer">
      <button type="button" class="aotp__btn aotp__btn--ghost" @click="limparPayload" v-if="fields.length">
        <fa-icon icon="eraser" /> Limpar valores
      </button>
      <button type="button" class="aotp__btn aotp__btn--secondary" :disabled="testandoLoading" @click="executarTeste">
        <fa-icon icon="vial" /> {{ testandoLoading ? 'Testando...' : 'Disparar teste' }}
      </button>
    </div>
  </div>
</template>

<script>
import { post } from '@/helpers/apiRequest';
import AcaoObjetoCampoSelect from './AcaoObjetoCampoSelect.vue';

// Captura o nome do campo entre crases no motivo (ex.: "O campo `operacaoId`...").
const MOTIVO_CAMPO_RE = /`([^`]+)`/;
// Captura o trecho "mas recebeu <valor>" para reescrita quando o campo veio vazio.
const MOTIVO_RECEBIDO_RE = /mas recebeu\s+[^.,;]*/i;

export default {
  name: 'AcaoObjetoTestPanel',
  components: { AcaoObjetoCampoSelect },
  props: {
    endpointBase: { type: String, required: true },
    acaoId:       { type: [Number, String], default: null },
    evento:       { type: String, default: '' },
    fields:       { type: Array,  default: () => [] },
    /**
     * Override de condição enviado ao backend no dry-run. Inclui:
     * { condition_type, condition_visual, condition_sql, condition_sql_connection,
     *   validar_todas_regras, regras }
     */
    condicaoOverride: { type: Object, required: true },
  },
  data() {
    return {
      aberto: false,
      testandoLoading: false,
      testValues: {},
      testResult: null,
      // Lista ordenada de nomes de variáveis adicionadas manualmente além das
      // já referenciadas pelas condições do gatilho.
      extrasNomes: [],
      adicionando: false,
      buscaCampo: '',
    };
  },
  computed: {
    testResultClass() {
      if (!this.testResult) return '';
      return `aotp__result--${this.testResult.tone}`;
    },
    camposUsados() {
      const cv = this.condicaoOverride && this.condicaoOverride.condition_visual;
      if (!cv || !Array.isArray(cv.grupos)) return [];
      const out = [];
      cv.grupos.forEach(g => {
        (g.condicoes || []).forEach(c => { if (c.campo) out.push(c.campo); });
      });
      return out;
    },
    camposPorNome() {
      const map = {};
      (this.fields || []).forEach(f => { map[f.name] = f; });
      return map;
    },
    camposCondicao() {
      // Mantemos a ordem em que os campos aparecem nas condições e
      // sintetizamos uma entrada (label = name, sem grupo) para campos
      // referenciados que ainda não constam em `this.fields` — caso contrário
      // o usuário não conseguiria preencher o valor de teste e o payload
      // sairia vazio, fazendo o backend reclamar de "valor null".
      const out = [];
      const seen = new Set();
      this.camposUsados.forEach(name => {
        if (seen.has(name)) return;
        seen.add(name);
        const f = this.camposPorNome[name];
        out.push(f || { name, label: name, grupo: null });
      });
      return out;
    },
    extrasAdicionados() {
      return this.extrasNomes
        .map(n => this.camposPorNome[n])
        .filter(Boolean);
    },
    camposCondicaoAgrupados() {
      return this.agruparPorModel(this.camposCondicao);
    },
    extrasAdicionadosAgrupados() {
      return this.agruparPorModel(this.extrasAdicionados);
    },
    camposDisponiveisParaAdicionar() {
      const jaPresentes = new Set([
        ...this.camposCondicao.map(c => c.name),
        ...this.extrasNomes,
      ]);
      return (this.fields || []).filter(f => !jaPresentes.has(f.name));
    },
  },
  methods: {
    abrir() {
      this.testResult = null;
      if (!this.aberto) this.inicializarTestValues();
      this.aberto = true;
    },
    fechar() {
      this.aberto = false;
      this.testandoLoading = false;
      this.testResult = null;
      this.testValues = {};
      this.extrasNomes = [];
      this.adicionando = false;
      this.buscaCampo = '';
      this.$emit('close');
    },
    agruparPorModel(campos) {
      if (!campos || !campos.length) return [];
      const grupos = {};
      campos.forEach(c => {
        const nome = c.grupo || 'Outros';
        if (!grupos[nome]) grupos[nome] = [];
        grupos[nome].push(c);
      });
      const ordem = Object.keys(grupos).sort((a, b) => {
        if (a === 'Campos do Evento') return -1;
        if (b === 'Campos do Evento') return 1;
        if (a === 'Campos Adicionais' || a === 'Outros') return 1;
        if (b === 'Campos Adicionais' || b === 'Outros') return -1;
        return a.localeCompare(b);
      });
      return ordem.map(nome => ({ nome, campos: grupos[nome] }));
    },
    iniciarAdicao() {
      this.adicionando = true;
      this.buscaCampo = '';
      this.$nextTick(() => {
        const picker = this.$refs.addPicker;
        const input = picker && picker.$refs && picker.$refs.searchInput;
        if (input) input.focus();
      });
    },
    cancelarAdicao() {
      this.adicionando = false;
      this.buscaCampo = '';
    },
    onCampoSelecionado(value) {
      // O CampoSelect emite 'input' a cada keystroke (busca) e também ao
      // selecionar um item. Só consideramos uma adição quando o valor casa
      // exatamente com o nome de um campo conhecido ainda não presente.
      this.buscaCampo = value || '';
      if (!value) return;
      const campo = this.camposPorNome[value];
      if (!campo) return;
      const jaPresentes = new Set([
        ...this.camposCondicao.map(c => c.name),
        ...this.extrasNomes,
      ]);
      if (jaPresentes.has(value)) return;
      this.extrasNomes.push(value);
      if (this.testValues[value] === undefined) {
        this.$set(this.testValues, value, '');
      }
      this.adicionando = false;
      this.buscaCampo = '';
    },
    removerExtra(name) {
      const idx = this.extrasNomes.indexOf(name);
      if (idx !== -1) this.extrasNomes.splice(idx, 1);
      this.$set(this.testValues, name, '');
    },
    inicializarTestValues() {
      const next = {};
      const prev = this.testValues || {};
      const init = (name) => {
        if (next[name] !== undefined) return;
        next[name] = prev[name] !== undefined ? prev[name] : '';
      };
      (this.fields || []).forEach(f => init(f.name));
      // Garante reatividade para campos referenciados nas condições mas que
      // não vieram no schema (ex.: condição recém-criada com campo novo).
      this.camposUsados.forEach(name => init(name));
      this.testValues = next;
    },
    setTestValue(name, value) {
      this.$set(this.testValues, name, value);
    },
    limparPayload() {
      const cleared = {};
      (this.fields || []).forEach(f => { cleared[f.name] = ''; });
      this.testValues = cleared;
      this.testResult = null;
    },
    coercePayloadValue(raw) {
      if (raw === null || raw === undefined) return null;
      const v = String(raw).trim();
      if (v === '') return null;
      if (v === 'true')  return true;
      if (v === 'false') return false;
      if (v === 'null')  return null;
      // Preserva strings com zero a esquerda (ex.: "00", "0001"), que costumam ser
      // codigos/identificadores e perderiam significado se convertidos para Number.
      const temZeroEsquerda = /^-?0\d/.test(v);
      if (!temZeroEsquerda && /^-?\d+$/.test(v))         return parseInt(v, 10);
      if (!temZeroEsquerda && /^-?\d+\.\d+$/.test(v))    return parseFloat(v);
      return raw;
    },
    buildPayload() {
      const payload = {};
      Object.keys(this.testValues || {}).forEach(k => {
        const v = this.coercePayloadValue(this.testValues[k]);
        if (v !== null && v !== '') payload[k] = v;
      });
      return payload;
    },
    campoTestadoEstaVazio(nomeCampo) {
      if (!nomeCampo || !this.testValues) return false;
      const valor = this.testValues[nomeCampo];
      if (valor === null || valor === undefined) return true;
      return String(valor).trim() === '';
    },
    // O backend retorna motivos como "O campo `X` deveria ser igual a Y, mas recebeu Z."
    // Quando o usuario nao preencheu o campo no painel de teste, reescrevemos o trecho
    // final para deixar explicito que o valor foi enviado vazio (e nao um literal "00"/"0").
    formatarMotivo(motivo) {
      if (typeof motivo !== 'string') return motivo;

      const matchCampo = motivo.match(MOTIVO_CAMPO_RE);
      if (!matchCampo) return motivo;

      const nomeCampo = matchCampo[1];
      if (!this.campoTestadoEstaVazio(nomeCampo)) return motivo;

      return motivo.replace(MOTIVO_RECEBIDO_RE, 'mas recebeu vazio');
    },
    executarTeste() {
      const payload = this.buildPayload();

      this.testandoLoading = true;
      this.testResult = null;

      const override = this.condicaoOverride || {};

      const url = this.acaoId
        ? `${this.endpointBase}/${this.acaoId}/testar`
        : `${this.endpointBase}/testar-rascunho`;
      const body = this.acaoId
        ? { payload, condicao: override }
        : { payload, ...override };

      post(url, body)
        .then(json => {
          if (json.erro) {
            this.testResult = {
              tone: 'error', icon: 'exclamation-triangle',
              label: 'Erro ao avaliar condição', detail: json.erro,
            };
          } else if (json.condicao_passou) {
            this.testResult = {
              tone: 'success', icon: 'check-circle',
              label: 'Condição passou',
              detail: 'O gatilho dispararia as ações com este payload.',
            };
          } else {
            const motivosRaw = Array.isArray(json.motivos) ? json.motivos : [];
            const motivos = motivosRaw.map(m => this.formatarMotivo(m));
            this.testResult = {
              tone: 'error', icon: 'exclamation-triangle',
              label: 'Condição reprovada',
              detail: motivos.length
                ? 'Com este payload o gatilho NÃO dispararia. Veja abaixo o que falhou:'
                : 'Com este payload o gatilho NÃO dispararia. Nenhuma das condições configuradas foi atendida.',
              motivos,
            };
          }
        })
        .catch(err => {
          this.testResult = {
            tone: 'error', icon: 'exclamation-triangle',
            label: 'Falha ao testar', detail: err.message || 'Erro de rede.',
          };
        })
        .finally(() => { this.testandoLoading = false; });
    },
    /**
     * Reaproveita event_data de um log: preenche todas as chaves recebidas
     * (mesmo as que nao estao em `fields`) e dispara o teste.
     */
    replayWith(eventData) {
      if (!eventData) return;
      this.testValues = {};
      this.extrasNomes = [];
      const camposCondNomes = new Set(this.camposCondicao.map(c => c.name));
      Object.keys(eventData).forEach(k => {
        const v = eventData[k];
        this.$set(this.testValues, k, v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v)));
        // Auto-adiciona como extra se for um campo conhecido não usado nas condições.
        if (this.camposPorNome[k] && !camposCondNomes.has(k) && this.extrasNomes.indexOf(k) === -1) {
          this.extrasNomes.push(k);
        }
      });
      this.aberto = true;
      this.$nextTick(() => this.executarTeste());
    },
    /**
     * Usa o payload de um log apenas para preencher os campos atuais (sem disparar).
     */
    usePayload(eventData) {
      if (!eventData) return;
      const next = {};
      (this.fields || []).forEach(f => {
        const v = eventData[f.name];
        next[f.name] = v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
      });
      // Auto-adiciona extras para campos que vieram preenchidos no payload.
      this.extrasNomes = [];
      const camposCondNomes = new Set(this.camposCondicao.map(c => c.name));
      (this.fields || []).forEach(f => {
        const v = eventData[f.name];
        if (v != null && !camposCondNomes.has(f.name)) {
          this.extrasNomes.push(f.name);
        }
      });
      this.testValues = next;
      this.aberto = true;
      this.testResult = null;
    },
  },
};
</script>

<style scoped>
.aotp {
  margin: 0 22px 12px;
  padding: 14px 16px;
  background: #fffbf2;
  border: 1px solid #f0d59b;
  border-radius: 10px;
}
.aotp__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}
.aotp__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #8a5a00;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.aotp__hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: #735200;
  line-height: 1.4;
}
.aotp__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 14px;
  margin-bottom: 12px;
}
@media (max-width: 700px) {
  .aotp__grid { grid-template-columns: 1fr; }
}
.aotp__field { display: flex; flex-direction: column; }
.aotp__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
.aotp__input {
  border-radius: 6px;
  border: 1px solid #dddce9;
  font-size: 13px;
  transition: all 0.15s ease;
}
.aotp__input:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 3px rgba(58, 62, 129, 0.1);
}
.aotp__var {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #f0d59b;
  color: #8a5a00;
  font-size: 11px;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
  font-weight: 500;
}
.aotp__group-label {
  font-size: 11px;
  font-weight: 700;
  color: #8a5a00;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
}
.aotp__group-label--extras {
  margin-top: 6px;
  border-top: 1px dashed #f0d59b;
  padding-top: 10px;
}
.aotp__model-group {
  margin-bottom: 10px;
}
.aotp__model-group:last-child {
  margin-bottom: 0;
}
.aotp__model-name {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #475467;
  background: #fff;
  border: 1px solid #f0d59b;
  border-radius: 4px;
  padding: 2px 8px;
  margin-bottom: 6px;
}
.aotp__add {
  margin-top: 4px;
  margin-bottom: 12px;
  border-top: 1px dashed #f0d59b;
  padding-top: 10px;
}
.aotp__add-picker {
  display: flex;
  align-items: center;
  gap: 10px;
}
.aotp__add-picker > .cs,
.aotp__add-picker > div:first-child {
  flex: 1 1 auto;
  min-width: 0;
}
.aotp__remove {
  margin-left: 6px;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid transparent;
  background: transparent;
  color: #98a2b3;
  font-size: 10px;
  cursor: pointer;
  padding: 0;
  vertical-align: middle;
  transition: all 0.15s ease;
}
.aotp__remove:hover {
  background: #fdecea;
  color: #b3261e;
  border-color: #e89189;
}
.aotp__toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 0;
  padding: 4px 0;
  color: #735200;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.aotp__toggle:hover { color: #8a5a00; }
.aotp__toggle--cancel { color: #98a2b3; }
.aotp__toggle--cancel:hover { color: #b3261e; }
.aotp__empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px dashed #f0d59b;
  border-radius: 8px;
  color: #735200;
  font-size: 12px;
  margin-bottom: 12px;
}
.aotp__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.aotp__result {
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 10px;
}
.aotp__result-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.aotp__motivos {
  list-style: none;
  margin: 10px 0 0;
  padding: 10px 12px 4px;
  background: rgba(255, 255, 255, 0.55);
  border-radius: 6px;
  border: 1px solid rgba(179, 38, 30, 0.25);
}
.aotp__motivos li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #8a1f18;
}
.aotp__motivos li .fa-icon,
.aotp__motivos li svg {
  margin-top: 2px;
  flex-shrink: 0;
}
.aotp__result--success {
  background: #ecfaee; color: #2e7d32; border: 1px solid #b9e7be;
}
.aotp__result--warn {
  background: #fff5e6; color: #8a5a00; border: 1px solid #f0d59b;
}
.aotp__result--error {
  background: #fdecea;
  color: #b3261e;
  border: 1px solid #e89189;
  box-shadow: 0 2px 8px rgba(179, 38, 30, 0.12);
}
.aotp__result--error .aotp__result-head strong {
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 700;
}

.aotp__icon-btn {
  width: 28px; height: 28px; border-radius: 6px; border: 1px solid #e4e7eb;
  background: #fff; color: #667085; cursor: pointer; transition: all 0.15s ease;
}
.aotp__icon-btn:hover { background: #f9fafb; color: #1d2939; }

.aotp__btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  font-family: inherit;
}
.aotp__btn--ghost {
  background: #fff;
  border-color: #dddce9;
  color: #475467;
}
.aotp__btn--ghost:hover { background: #f7f7fc; color: #1d2939; }
.aotp__btn--secondary {
  background: #edf8f9;
  color: #2298a3;
  border-color: #c5e6e9;
}
.aotp__btn--secondary:hover { background: #2298a3; color: #fff; border-color: #2298a3; }
.aotp__btn[disabled] { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
</style>
